import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { SERVICE_GROUPS, STATUSES, isKB, isYoga, LOCATIONS, DISCOUNT_TYPES, PAYMENT_METHODS, PAYMENT_STATUSES, SERVICE_CATEGORIES, SPA_CATEGORIES, formatRp, computeFinal, discountAmount, todayWITA } from "@/lib/constants";
import { UserCircle, Baby, CalendarBlank, Note, CurrencyDollar, UsersThree } from "@phosphor-icons/react";

const empty = {
  nama_lengkap: "",
  no_whatsapp: "",
  usia: "",
  kehamilan_ke: "",
  jumlah_persalinan: "",
  jumlah_keguguran: "",
  usia_kehamilan_minggu: "",
  hpl: "",
  keluhan: "",
  tanggal_kunjungan: "",
  jam_kunjungan: "",
  catatan_admin: "",
  status: "Baru",
  jenis_layanan: "",
  kategori_layanan: "",
  service_id: "",
  petugas_nama: "",
  tanggal_layanan: "",
  lokasi: "",
  harga_normal: 0,
  jenis_diskon: "Tidak ada",
  nilai_diskon: 0,
  potongan_lainnya: 0,
  alasan_diskon: "",
  biaya_homecare: 0,
  biaya_bahan: 0,
  harga_final: null,
  harga_final_manual: false,
  alasan_perubahan_harga: "",
  metode_pembayaran: "Cash",
  status_pembayaran: "Belum Bayar",
};

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm outline-none transition-colors focus:border-rose-400 focus:ring-2 focus:ring-rose-100";
const labelCls = "mb-1.5 block text-xs font-semibold text-slate-600";

function Section({ icon: Icon, title, children }) {
  return (
    <div className="rounded-2xl border border-rose-100/70 bg-white p-5 shadow-[0_2px_10px_-3px_rgba(200,124,136,0.1)] sm:p-6">
      <div className="mb-4 flex items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#EEE6FF] text-[#6A38C7]">
          <Icon size={18} weight="duotone" />
        </span>
        <h3 className="font-heading text-base font-bold text-slate-800">{title}</h3>
      </div>
      {children}
    </div>
  );
}

export default function ReservationForm({ initial, onSubmit, submitting, submitLabel = "Simpan Reservasi", publicMode = false, practiceCheck = false, onPracticeChange }) {
  const [form, setForm] = useState({ ...empty, ...(initial || {}) });
  const [error, setError] = useState("");
  const [practice, setPractice] = useState(null); // eligible practice-availability payload, or null
  const [practiceError, setPracticeError] = useState("");
  const [staffList, setStaffList] = useState([]);
  const [servicesList, setServicesList] = useState([]);
  const [catList, setCatList] = useState([]);

  useEffect(() => {
    if (publicMode) return;
    api.get("/staff").then((r) => setStaffList(r.data)).catch(() => {});
    api.get("/services").then((r) => setServicesList(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCatList(r.data)).catch(() => {});
  }, [publicMode]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Single source of truth for practice detection; notify parent (AddReservation) so it can route submit.
  const applyPractice = (p) => {
    setPractice(p);
    if (onPracticeChange) onPracticeChange(p);
  };

  const handleService = (value) => {
    const svc = servicesList.find((s) => s.nama === value);
    setForm((f) => ({
      ...f,
      jenis_layanan: value,
      service_id: svc ? svc.id : "",
      kategori_layanan: svc ? svc.kategori : f.kategori_layanan,
      harga_normal: svc ? svc.harga_normal : f.harga_normal,
    }));
  };

  // Detect an active, ELIGIBLE practice/queue session for the chosen service+petugas+date (Gerai, non-Homecare).
  // Backend returns has_session:true but withOUT `used` when the service is ineligible (petugas not allowed or
  // fixed/fixed_staff class) — those are treated as regular so the manual time stays visible.
  useEffect(() => {
    if (!practiceCheck) { applyPractice(null); setPracticeError(""); return; }
    const svcId = form.service_id;
    const pet = form.petugas_nama;
    const tgl = form.tanggal_kunjungan;
    const isHomecare = form.lokasi === "Homecare" || form.lokasi === "Online";
    // Clear any stale detection from the previous selection before re-checking.
    applyPractice(null);
    setPracticeError("");
    if (!svcId || !pet || !tgl || isHomecare) return;
    let ignore = false;
    api.get("/public/practice-availability", { params: { petugas: pet, service_id: svcId, date: tgl } })
      .then((r) => {
        if (ignore) return;
        const d = r.data;
        if (d && d.has_session && d.used !== undefined) applyPractice(d); // eligible session
        else applyPractice(null); // no session OR ineligible service -> regular flow
      })
      .catch(() => {
        if (ignore) return;
        applyPractice(null);
        setPracticeError("Gagal memeriksa jadwal praktik. Silakan coba lagi (sesi tidak dianggap tersedia).");
      });
    return () => { ignore = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceCheck, form.service_id, form.petugas_nama, form.tanggal_kunjungan, form.lokasi]);

  const practiceActive = practiceCheck && !!practice;

  const catOrder = catList.length ? catList.filter((c) => c.aktif).map((c) => c.nama) : SERVICE_CATEGORIES;
  const serviceOptgroups = catOrder
    .map((c) => ({ label: c, options: servicesList.filter((s) => s.kategori === c && s.aktif) }))
    .filter((g) => g.options.length);

  const kbSelected = isKB(form.jenis_layanan);
  const catRequires = ["Pemeriksaan Kehamilan", "Konsultasi", "Prenatal Yoga"].includes(form.kategori_layanan || "");
  const kehamilanRequired = form.jenis_layanan && (publicMode ? !kbSelected : catRequires);
  const isSpa = SPA_CATEGORIES.includes(form.kategori_layanan);
  const liveFinal = computeFinal(form);

  const submit = (e) => {
    e.preventDefault();
    setError("");
    if (!form.jenis_layanan) return setError("Silakan pilih jenis layanan.");
    if (kehamilanRequired && !form.usia_kehamilan_minggu)
      return setError("Usia kehamilan wajib diisi untuk layanan kehamilan / Prenatal Yoga.");
    if (practiceActive) {
      if (practice.can_book === false)
        return setError("Kuota / rentang waktu sesi praktik sudah penuh. Silakan pilih tanggal lain.");
      // Jam manual tidak diperlukan: backend mengalokasi jam & nomor antrean otomatis.
    } else if (!form.jam_kunjungan) {
      return setError("Silakan isi jam kunjungan.");
    }
    if (!publicMode && form.harga_final_manual && !form.alasan_perubahan_harga)
      return setError("Isi alasan perubahan harga final.");

    const toNum = (v) => (v === "" || v === null ? null : Number(v));
    const payload = {
      ...form,
      usia: toNum(form.usia),
      kehamilan_ke: toNum(form.kehamilan_ke),
      jumlah_persalinan: toNum(form.jumlah_persalinan),
      jumlah_keguguran: toNum(form.jumlah_keguguran),
      usia_kehamilan_minggu: toNum(form.usia_kehamilan_minggu),
      hpl: form.hpl || null,
      harga_normal: Number(form.harga_normal || 0),
      nilai_diskon: Number(form.nilai_diskon || 0),
      potongan_lainnya: Number(form.potongan_lainnya || 0),
      biaya_homecare: Number(form.biaya_homecare || 0),
      biaya_bahan: Number(form.biaya_bahan || 0),
      harga_final: form.harga_final_manual ? Number(form.harga_final || 0) : null,
      alamat_data: (form.alamat_data && typeof form.alamat_data === "object") ? form.alamat_data : null,
    };
    onSubmit(payload);
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="reservation-form">
      <Section icon={UserCircle} title="Data Diri Pasien">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className={labelCls}>Nama Lengkap Pasien *</label>
            <input
              data-testid="input-nama"
              required
              value={form.nama_lengkap}
              onChange={(e) => set("nama_lengkap", e.target.value)}
              className={inputCls}
              placeholder="Nama pasien"
            />
          </div>
          <div>
            <label className={labelCls}>Nomor WhatsApp *</label>
            <input
              data-testid="input-whatsapp"
              required
              value={form.no_whatsapp}
              onChange={(e) => set("no_whatsapp", e.target.value)}
              className={inputCls}
              placeholder="0812xxxxxxxx"
            />
          </div>
          <div>
            <label className={labelCls}>Usia Pasien (tahun)</label>
            <input
              data-testid="input-usia"
              type="number"
              min="0"
              value={form.usia}
              onChange={(e) => set("usia", e.target.value)}
              className={inputCls}
              placeholder="28"
            />
          </div>
        </div>
      </Section>

      <Section icon={Baby} title="Data Medis Kehamilan">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className={labelCls}>Kehamilan ke-</label>
            <input
              data-testid="input-kehamilan-ke"
              type="number"
              min="0"
              value={form.kehamilan_ke}
              onChange={(e) => set("kehamilan_ke", e.target.value)}
              className={inputCls}
              placeholder="1"
            />
          </div>
          <div>
            <label className={labelCls}>
              Usia Kehamilan (minggu) {kehamilanRequired ? "*" : ""}
            </label>
            <input
              data-testid="input-usia-kehamilan"
              type="number"
              min="0"
              value={form.usia_kehamilan_minggu}
              onChange={(e) => set("usia_kehamilan_minggu", e.target.value)}
              className={inputCls}
              placeholder={kbSelected ? "Tidak wajib (KB)" : "cth. 20"}
            />
          </div>
          <div>
            <label className={labelCls}>Jumlah Persalinan Sebelumnya</label>
            <input
              data-testid="input-persalinan"
              type="number"
              min="0"
              value={form.jumlah_persalinan}
              onChange={(e) => set("jumlah_persalinan", e.target.value)}
              className={inputCls}
              placeholder="0"
            />
          </div>
          <div>
            <label className={labelCls}>Jumlah Keguguran (jika ada)</label>
            <input
              data-testid="input-keguguran"
              type="number"
              min="0"
              value={form.jumlah_keguguran}
              onChange={(e) => set("jumlah_keguguran", e.target.value)}
              className={inputCls}
              placeholder="0"
            />
          </div>
          <div>
            <label className={labelCls}>HPL (Hari Perkiraan Lahir)</label>
            <input
              data-testid="input-hpl"
              type="date"
              value={form.hpl || ""}
              onChange={(e) => set("hpl", e.target.value)}
              className={inputCls}
            />
          </div>
        </div>
      </Section>

      <Section icon={CalendarBlank} title="Detail Layanan & Kunjungan">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className={labelCls}>Jenis Layanan *</label>
            <select
              data-testid="input-jenis-layanan"
              required
              value={form.jenis_layanan}
              onChange={(e) => (publicMode ? set("jenis_layanan", e.target.value) : handleService(e.target.value))}
              className={inputCls}
            >
              <option value="">-- Pilih Layanan --</option>
              {publicMode
                ? SERVICE_GROUPS.map((g) => (
                    <optgroup key={g.label} label={g.label}>
                      {g.options.map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </optgroup>
                  ))
                : serviceOptgroups.map((g) => (
                    <optgroup key={g.label} label={g.label}>
                      {g.options.map((o) => (
                        <option key={o.id} value={o.nama}>{`${o.nama} — ${formatRp(o.harga_normal)}`}</option>
                      ))}
                    </optgroup>
                  ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Tanggal Kunjungan *</label>
            <input
              data-testid="input-tanggal-kunjungan"
              type="date"
              required
              value={form.tanggal_kunjungan}
              onChange={(e) => set("tanggal_kunjungan", e.target.value)}
              className={inputCls}
            />
          </div>
          {practiceActive ? (
            <div data-testid="practice-info-admin">
              <label className={labelCls}>Jam Kunjungan</label>
              <div className="rounded-xl bg-[#EEF6FF] px-3 py-2.5 text-xs text-sky-700">
                🩺 Jadwal praktik {form.petugas_nama}: {practice.window}.
                <span className="mt-1 block">Jam pelayanan dan nomor antrean ditentukan otomatis setelah reservasi berhasil.</span>
                {practice.can_book === false && (
                  <span data-testid="practice-full-admin" className="mt-1 block font-bold text-rose-600">Kuota sesi praktik penuh. Silakan pilih tanggal lain.</span>
                )}
              </div>
            </div>
          ) : (
            <div>
              <label className={labelCls}>Jam Kunjungan *</label>
              <input
                data-testid="input-jam-kunjungan"
                type="time"
                required
                value={form.jam_kunjungan}
                onChange={(e) => set("jam_kunjungan", e.target.value)}
                className={inputCls}
              />
              {practiceCheck && practiceError && (
                <p data-testid="practice-check-error" className="mt-1 text-xs text-amber-600">{practiceError}</p>
              )}
            </div>
          )}
          <div className="sm:col-span-2">
            <label className={labelCls}>Keluhan / Alasan Konsultasi</label>
            <textarea
              data-testid="input-keluhan"
              rows={2}
              value={form.keluhan}
              onChange={(e) => set("keluhan", e.target.value)}
              className={inputCls}
              placeholder="cth. Mual di pagi hari, kontrol rutin..."
            />
          </div>
          {!publicMode && (
            <div>
              <label className={labelCls}>Status Reservasi</label>
              <select
                data-testid="input-status"
                value={form.status}
                onChange={(e) => set("status", e.target.value)}
                className={inputCls}
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </Section>

      {!publicMode && (
        <Section icon={Note} title="Catatan Admin">
          <textarea
            data-testid="input-catatan-admin"
            rows={2}
            value={form.catatan_admin}
            onChange={(e) => set("catatan_admin", e.target.value)}
            className={inputCls}
            placeholder="Catatan internal (opsional)"
          />
        </Section>
      )}

      {!publicMode && (
        <Section icon={UsersThree} title="Petugas & Lokasi Layanan">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Petugas yang Melayani</label>
              <select data-testid="input-petugas" value={form.petugas_nama || ""} onChange={(e) => set("petugas_nama", e.target.value)} className={inputCls}>
                <option value="">-- Pilih Petugas --</option>
                {staffList.map((s) => <option key={s.id} value={s.nama}>{s.nama} ({s.jenis})</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Lokasi Layanan</label>
              <select data-testid="input-lokasi" value={form.lokasi || ""} onChange={(e) => set("lokasi", e.target.value)} className={inputCls}>
                <option value="">-- Pilih Lokasi --</option>
                {LOCATIONS.map((l) => <option key={l}>{l}</option>)}
              </select>
              {form.lokasi === "Online" && (
                <label className="mt-2 flex items-start gap-2 rounded-lg border border-teal-200 bg-teal-50 p-2 text-[11px] text-teal-800" data-testid="input-online-consent-wrap">
                  <input type="checkbox" data-testid="input-online-consent" checked={!!form.telekonsultasi_consent} onChange={(e) => set("telekonsultasi_consent", e.target.checked)} className="mt-0.5" />
                  <span>Pasien telah menyetujui ketentuan telekonsultasi (wajib). Room chat dibuka sesuai gerbang pembayaran layanan.</span>
                </label>
              )}
            </div>
            <div>
              <label className={labelCls}>Tanggal Layanan</label>
              <input data-testid="input-tanggal-layanan" type="date" value={form.tanggal_layanan || ""} onChange={(e) => set("tanggal_layanan", e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Kategori Layanan</label>
              <input data-testid="input-kategori" value={form.kategori_layanan || ""} readOnly className={inputCls + " bg-slate-50"} placeholder="Otomatis dari layanan" />
            </div>
          </div>
        </Section>
      )}

      {!publicMode && (
        <Section icon={CurrencyDollar} title="Harga & Pembayaran">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Harga Normal (Rp)</label>
              <input data-testid="input-harga-normal" type="number" value={form.harga_normal} onChange={(e) => set("harga_normal", e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Jenis Diskon</label>
              <select data-testid="input-jenis-diskon" value={form.jenis_diskon} onChange={(e) => set("jenis_diskon", e.target.value)} className={inputCls}>{DISCOUNT_TYPES.map((d) => <option key={d}>{d}</option>)}</select>
            </div>
            {form.jenis_diskon !== "Tidak ada" && (
              <div>
                <label className={labelCls}>Nilai Diskon {form.jenis_diskon === "Persentase" ? "(%)" : "(Rp)"}</label>
                <input data-testid="input-nilai-diskon" type="number" value={form.nilai_diskon} onChange={(e) => set("nilai_diskon", e.target.value)} className={inputCls} />
              </div>
            )}
            <div>
              <label className={labelCls}>Potongan Lainnya (Rp)</label>
              <input data-testid="input-potongan" type="number" value={form.potongan_lainnya} onChange={(e) => set("potongan_lainnya", e.target.value)} className={inputCls} />
            </div>
            {isSpa && (
              <>
                <div>
                  <label className={labelCls}>Biaya Tambahan Homecare (Rp)</label>
                  <input data-testid="input-biaya-homecare" type="number" value={form.biaya_homecare} onChange={(e) => set("biaya_homecare", e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Biaya Bahan / Habis Pakai (Rp)</label>
                  <input data-testid="input-biaya-bahan" type="number" value={form.biaya_bahan} onChange={(e) => set("biaya_bahan", e.target.value)} className={inputCls} />
                </div>
              </>
            )}
            <div className="sm:col-span-2">
              <label className={labelCls}>Alasan Diskon / Potongan</label>
              <input data-testid="input-alasan-diskon" value={form.alasan_diskon || ""} onChange={(e) => set("alasan_diskon", e.target.value)} className={inputCls} placeholder="cth. Promo, paket, pasien lama..." />
            </div>
          </div>

          <div className="mt-4 rounded-xl bg-[#F7F0FF] p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">Harga Final (otomatis)</span>
              <span className="font-heading text-2xl font-extrabold text-[#6A38C7]" data-testid="harga-final-auto">{formatRp(liveFinal)}</span>
            </div>
            <label className="mt-3 flex items-center gap-2 text-sm text-slate-600">
              <input type="checkbox" data-testid="toggle-manual-final" checked={!!form.harga_final_manual} onChange={(e) => { const c = e.target.checked; setForm((f) => ({ ...f, harga_final_manual: c, harga_final: c ? (f.harga_final ?? liveFinal) : null })); }} /> Ubah harga final manual
            </label>
            {form.harga_final_manual && (
              <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                <input data-testid="input-harga-final" type="number" value={form.harga_final ?? ""} onChange={(e) => set("harga_final", e.target.value)} className={inputCls} placeholder="Harga final (Rp)" />
                <input data-testid="input-alasan-harga" value={form.alasan_perubahan_harga || ""} onChange={(e) => set("alasan_perubahan_harga", e.target.value)} className={inputCls} placeholder="Alasan perubahan harga (wajib)" />
              </div>
            )}
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Metode Pembayaran</label>
              <select data-testid="input-metode" value={form.metode_pembayaran || "Cash"} onChange={(e) => set("metode_pembayaran", e.target.value)} className={inputCls}>{PAYMENT_METHODS.map((m) => <option key={m}>{m}</option>)}</select>
            </div>
            <div>
              <label className={labelCls}>Status Pembayaran</label>
              <select data-testid="input-status-bayar" value={form.status_pembayaran} onChange={(e) => set("status_pembayaran", e.target.value)} className={inputCls}>{PAYMENT_STATUSES.map((m) => <option key={m}>{m}</option>)}</select>
            </div>
          </div>
        </Section>
      )}

      {error && (
        <p data-testid="form-error" className="rounded-lg bg-rose-50 px-4 py-3 text-sm text-rose-600">
          {error}
        </p>
      )}

      <div className="flex justify-end">
        <button
          data-testid="submit-reservation"
          type="submit"
          disabled={submitting || (practiceActive && practice.can_book === false)}
          className="rounded-xl bg-[#7C4DE0] px-8 py-3 text-sm font-bold text-white shadow-sm transition-all hover:bg-[#6A38C7] active:scale-95 disabled:opacity-60"
        >
          {submitting ? "Menyimpan..." : submitLabel}
        </button>
      </div>
    </form>
  );
}
